import React from 'react';
import tw, { css } from 'twin.macro';
import useReunionModal from '@teetsh/app/src/containers/Planner/Main/ReunionModal/useReunionModal';
import { getDurationInHoursAndMinutesBetween } from 'utils/time';
import addMinutes from 'utils/time/addMinutes';
import { ReunionWithCategory } from 'types/reunions';
import { DocumentTextIcon, PaperClipIcon } from '@heroicons/react/20/solid';
import _isEmpty from 'lodash-es/isEmpty';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import { colors, getColorHexOrFallback } from 'components/colors';
import ensureAccessibleContrastLightening from 'utils/style/ensureAccessibleContrastLightening';
import { Tag } from 'components/Tag';
import { buildPlannerEventHours } from 'containers/Planner/buildPlannerEventHours';

interface Props {
  reunion: ReunionWithCategory;
}

export function PlannerEvent(props: Props) {
  const { reunion } = props;
  const { openReunionModal } = useReunionModal();
  const { t } = useTranslation(['planner']);
  const { category } = reunion;
  const { locale } = useUser();
  const categoryColorHex = getColorHexOrFallback(category?.color);
  const backgroundColor = ensureAccessibleContrastLightening(
    categoryColorHex,
    colors['text-neutral-quiet'],
  );
  return (
    <div
      css={[
        tw`rounded-lg p-2 space-y-1`,
        css`
          background-color: ${backgroundColor};
        `,
      ]}
    >
      <div tw="flex">
        <Tag outlined size="sm" color={category?.color}>
          {category.name}
        </Tag>
      </div>
      <div tw="px-2 flex items-center justify-between text-neutral-quiet text-xs leading-none">
        <span>
          {buildPlannerEventHours(
            reunion,
            locale,
            t('planner:allDay'),
            'start - end',
          )}
        </span>
        {(reunion.lastingInMinutes || 0) > 0 && (
          <span>
            {getDurationInHoursAndMinutesBetween(
              reunion.date,
              addMinutes(reunion.date, reunion.lastingInMinutes!),
            )}
          </span>
        )}
      </div>
      <div
        role="presentation"
        tw="px-2 text-sm flex-grow hover:underline cursor-pointer font-semibold inline-flex items-center leading-[19px]"
        onClick={() =>
          openReunionModal({
            reunion: {
              ...reunion,
              categoryId: reunion.category?.id,
            },
          })
        }
      >
        {(reunion.attachments || []).length > 0 && (
          <PaperClipIcon tw="inline-block mr-1 h-4 w-4" />
        )}
        {!_isEmpty(reunion.description) && (
          <DocumentTextIcon tw="inline-block mr-1 h-4 w-4" />
        )}
        {reunion.name ? reunion.name : <>{t('planner:evenementSansTitre')}</>}
      </div>
    </div>
  );
}
